/**
 * "The user picked a language" — one behaviour, shared by every control that offers the
 * choice to a signed-in user: the Profile screen's picker (`routes/ProfileScreen.tsx`) and
 * the TopBar toggle (`shell/AppShell.tsx`'s `LanguageToggle`). Before this hook existed, each
 * control had its own copy of "switch, persist, revert on failure" — which is how the TopBar
 * toggle ended up with only two thirds of it: it switched and remembered, but never
 * persisted, so it silently undid itself on the next silent token refresh
 * (`app/auth.tsx`'s `applyProfileLanguage`, which now applies the profile locale
 * unconditionally). One definition, consumed twice, is what keeps a future fix to the
 * revert path from landing in one control and rotting in the other.
 *
 * The switch is optimistic — `localStorage` and `i18n` move immediately, before
 * `PATCH /api/v1/auth/me` settles, so it feels instant wherever it is made. A failed save
 * reverts both and surfaces the error as a toast, the same failure-signal every other write
 * in the portal uses (`apiErrorMessage` + `toast.error`).
 *
 * `language` is read off `useTranslation()`'s own `i18n.language` rather than tracked in a
 * private `useState`. Both consuming controls can be mounted at once — the TopBar sits
 * inside `AppShell`, which wraps `ProfileScreen`'s own content, so a Profile screen visit
 * always has both on screen together — and a `useState` per consumer would desync the
 * instant one control changed language and the other's own copy did not independently
 * re-render. Reading the shared `i18next` instance is what keeps every mounted control
 * showing the same answer without any coordination between them.
 *
 * Only writes to the server when a principal exists. With nobody signed in — the login
 * screen, the password-reset/activation screen — there is no profile to write to, and the
 * choice has to degrade to the same local-only behaviour `ui/LanguagePicker.tsx` already
 * has (state + `localStorage` + `i18n`, nothing else). `LanguagePicker.tsx` is deliberately
 * left as its own hand-rolled version rather than switched to this hook, precisely so it can
 * never fire an unauthenticated `PATCH`.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { unwrap } from '@deca/api'

import { api } from './api.ts'
import { apiErrorMessage } from './errors.ts'
import { useAuth } from './auth.tsx'
import { rememberLanguage, type Language } from './i18n.ts'
import { useToast } from '../ui/Toast.tsx'

export function useLanguageChoice(): { language: Language; choose: (next: Language) => Promise<void> } {
  const { t, i18n: instance } = useTranslation()
  const toast = useToast()
  const { principal } = useAuth()

  const language: Language = instance.language.startsWith('en') ? 'en' : 'es'

  const choose = useCallback(
    async (next: Language) => {
      const previous = language
      rememberLanguage(next)
      await instance.changeLanguage(next)

      // No profile to write to yet — the anonymous surfaces stop here, same as
      // `LanguagePicker.tsx`'s own local-only switch.
      if (!principal) return

      try {
        await unwrap(api.PATCH('/api/v1/auth/me', { body: { locale: next } }))
      } catch (caught) {
        rememberLanguage(previous)
        await instance.changeLanguage(previous)
        toast.error(apiErrorMessage(caught, t))
      }
    },
    [language, principal, instance, t, toast],
  )

  return { language, choose }
}
